import { useEffect, useState } from 'react';
import { ArrowRight, Layers, Wifi, MapPin } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { currentStage } from '../data/projectStages';
import { useScrollToSection } from '../hooks/useScrollToSection';

function AnimatedCounter({ end, duration = 2 }: { end: number; duration?: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}</span>;
}

export function Hero() {
  const [activeImage, setActiveImage] = useState(0);
  const { scrollToSection } = useScrollToSection();

  const images = [
    '/images/znl-1.webp',
    '/images/znl2.webp',
    '/images/master-corregido.webp',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: activeImage === index ? 1 : 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            <ImageWithFallback src={image} alt={`Free Zone Logistic Park ${index + 1}`} className="w-full h-full object-cover" />
          </motion.div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/98 to-white/75" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(25,40,113,0.05),transparent_35%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 text-primary"
              >
                <span className="text-xs uppercase tracking-[0.18em] text-primary/60 font-semibold">Etapa activa</span>
                <span className="text-sm font-bold">{currentStage.shortName}</span>
                <span className="text-xs text-primary/60">{currentStage.period}</span>
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl text-primary tracking-tight font-bold leading-[1.02] mb-6"
            >
              Free Zone
              <span className="block text-secondary font-semibold">Logistics Park</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed mb-8 font-medium"
            >
              Ofi-bodegas Clase A en la Zona Libre de Colón diseñadas para operaciones logísticas eficientes y crecimiento empresarial.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="bg-secondary text-white hover:bg-secondary/90 shadow-sm"
                onClick={() => scrollToSection('contact')}
              >
                Solicitar información
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 font-semibold"
                onClick={() => scrollToSection('about')}
              >
                Ver presentación
              </Button>
            </motion.div>

          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="rounded-[1.75rem] overflow-hidden border border-primary/10 shadow-2xl bg-white/40 backdrop-blur-sm">
              <div className="relative aspect-[4/5]">
                <ImageWithFallback src={images[activeImage]} alt="Vista del parque" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
